import { useState, useMemo, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  CalendarClock,
  CalendarCheck,
  Banknote,
  Wallet,
  FileBarChart2,
  Settings,
  LogOut,
  ShieldCheck,
  Building2,
  Scale,
  ScrollText,
  LifeBuoy,
  LayoutDashboard,
  QrCode,
  Sparkles,
  ClipboardList,
  Ticket,
  CheckSquare,
  BarChart3,
  Briefcase,
  UserCog,
  UserMinus,
  Calculator,
  Gift,
  Banknote as BanknoteIcon,
  Tv,
  Radio,
  Server,
  Truck,
  Fuel,
  UserCheck,
  ListChecks,
  SlidersHorizontal,
  Award,
  ClipboardCheck,
  Megaphone,
  CalendarRange,
  Trophy,
  Menu,
  X,
  ChevronRight,
  Search,
  Users as UsersGroupIcon,
  Clock,
  CalendarDays,
  PiggyBank,
  Star,
  Activity,
  Wrench,
  Fingerprint,
  MessageSquare,
  MapPin,
  PackageOpen,
  User,
} from "lucide-react";
import PrincipalSwitcher from "@/components/PrincipalSwitcher";
import BirthdayBell from "@/components/BirthdayBell";
import AnniversaryBell from "@/components/AnniversaryBell";

// Flat item definition — same as before, but tagged with a `group` key.
// Groups render as collapsible sections in the sidebar.
const NAV_ITEMS = [
  // ─── Dashboard (no group, always at top) ─────────────────────────────
  { to: "/", label: "Overview", icon: LayoutDashboard, group: "_top", roles: ["super_admin", "hr", "unit_manager"] },

  // ─── People ──────────────────────────────────────────────────────────
  { to: "/employees", label: "Employees", icon: UsersIcon, group: "people", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/admin/org-chart", label: "Org Chart", icon: Building2, group: "people", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/admin/recruitment", label: "Recruitment", icon: Briefcase, group: "people", roles: ["super_admin", "senior_management", "hr", "hod", "unit_manager", "team_leader"] },
  { to: "/hr/asset-handover", label: "Asset Handover", icon: PackageOpen, group: "people", roles: ["super_admin", "hr", "it_admin", "accounts", "senior_management", "ea", "reporting_manager"] },
  { to: "/admin/communications", label: "Communications", icon: MessageSquare, group: "people", roles: ["super_admin", "hr"] },

  // ─── Attendance ──────────────────────────────────────────────────────
  { to: "/attendance", label: "Attendance", icon: CalendarClock, group: "attendance", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/attendance-v2", label: "Attendance v2 (Beta)", icon: ListChecks, group: "attendance", roles: ["super_admin", "hr"] },
  { to: "/admin/punch-attendance", label: "Punch Attendance", icon: Tv, group: "attendance", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/live-attendance", label: "Live Attendance", icon: Radio, group: "attendance", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/attendance/monthly", label: "Monthly Summary", icon: FileBarChart2, group: "attendance", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/admin/biometric-devices", label: "Biometric Devices", icon: Server, group: "attendance", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/admin/biometric-matching", label: "Biometric Matching", icon: UserCheck, group: "attendance", roles: ["super_admin", "hr"] },
  { to: "/admin/biometric-mappings", label: "Biometric Mappings", icon: Fingerprint, group: "attendance", roles: ["super_admin", "hr"] },

  // ─── Leave ────────────────────────────────────────────────────────────
  { to: "/leave-ledger", label: "Leave Ledger", icon: Wallet, group: "leave", roles: ["super_admin", "hr", "unit_manager"] },
  { to: "/leaves/approvals", label: "Leave Approvals", icon: CalendarCheck, group: "leave", roles: ["super_admin", "hr"] },
  { to: "/admin/leave-config", label: "Leave Config", icon: SlidersHorizontal, group: "leave", roles: ["super_admin", "hr"] },
  { to: "/admin/holidays", label: "Holidays", icon: CalendarClock, group: "leave", roles: ["super_admin", "hr"] },
  { to: "/admin/optional-leaves", label: "Optional Leaves", icon: Sparkles, group: "leave", roles: ["super_admin", "hr"] },

  // ─── Payroll & Finance ────────────────────────────────────────────────
  { to: "/payroll", label: "Payroll", icon: Banknote, group: "payroll", roles: ["super_admin", "hr"] },
  { to: "/admin/payroll-rules", label: "Payroll Rules", icon: ScrollText, group: "payroll", roles: ["super_admin", "hr"] },
  { to: "/payroll/comparison", label: "Payroll Comparison", icon: Scale, group: "payroll", roles: ["super_admin", "hr"] },
  { to: "/admin/salary-calculator", label: "Salary Calculator", icon: Calculator, group: "payroll", roles: ["super_admin", "hr"] },
  { to: "/admin/advances", label: "Advances", icon: BanknoteIcon, group: "payroll", roles: ["super_admin", "hr", "senior_management", "accounts", "unit_manager"] },
  { to: "/admin/advances/reports", label: "Advance Reports", icon: FileBarChart2, group: "payroll", roles: ["super_admin", "hr", "accounts", "unit_manager"] },
  { to: "/admin/financial-years", label: "Financial Years", icon: CalendarRange, group: "payroll", roles: ["super_admin", "hr", "senior_management", "accounts"] },
  { to: "/admin/festivals", label: "Festivals & Gifts", icon: Gift, group: "payroll", roles: ["super_admin", "hr", "senior_management"] },
  { to: "/vendor-payments", label: "Vendor Scan & Pay", icon: QrCode, group: "payroll", roles: ["super_admin", "senior_management", "accounts"] },

  // ─── Performance ─────────────────────────────────────────────────────
  { to: "/admin/appraisal", label: "Appraisal Hub", icon: Award, group: "performance", roles: ["super_admin", "hr"] },
  { to: "/appraisal/reviews", label: "My Reviews", icon: ClipboardCheck, group: "performance", roles: ["super_admin", "hr", "senior_mgmt", "unit_manager"] },
  { to: "/admin/appraisal-dashboard", label: "Appraisal Due", icon: CalendarClock, group: "performance", roles: ["super_admin", "hr"] },
  { to: "/admin/performance", label: "Performance Scoring", icon: Trophy, group: "performance", roles: ["super_admin", "hr"] },

  // ─── Logistics ───────────────────────────────────────────────────────
  { to: "/logistics/hub", label: "Logistics Hub", icon: Truck, group: "logistics", roles: ["super_admin", "hr", "logistics_manager", "logistics_executive"] },
  { to: "/logistics/driver-attendance", label: "Driver Attendance", icon: Truck, group: "logistics", roles: ["super_admin", "hr", "logistics_manager", "logistics_executive"] },
  { to: "/logistics/fuel", label: "Fuel & Mileage", icon: Fuel, group: "logistics", roles: ["super_admin", "hr", "logistics_manager", "logistics_executive", "accounts"] },
  { to: "/logistics/fastag-map", label: "FASTag Map", icon: MapPin, group: "logistics", roles: ["super_admin", "hr", "logistics_manager", "logistics_executive", "accounts", "senior_management"] },
  { to: "/logistics/fleet-compliance", label: "Fleet Compliance", icon: ShieldCheck, group: "logistics", roles: ["super_admin", "senior_management", "hr", "logistics_manager", "logistics_executive", "accounts", "ea"], badgeKey: "fleet_alerts" },
  { to: "/logistics/vehicle-ops", label: "Vehicle Ops", icon: Wrench, group: "logistics", roles: ["super_admin", "senior_management", "hr", "logistics_manager", "logistics_executive", "accounts", "ea"] },
  { to: "/logistics/fleet-status", label: "Fleet Status", icon: Truck, group: "logistics", roles: ["super_admin", "senior_management", "hr", "logistics_manager", "logistics_executive", "accounts", "ea"] },
  { to: "/logistics/lm-mobile", label: "LM Mobile (Assign+Trip)", icon: Truck, group: "logistics", roles: ["super_admin", "logistics_manager", "logistics_executive", "ea"] },

  // ─── Operations (Requests, Tasks, Checklists, Broadcasts) ────────────
  { to: "/approvals", label: "Approvals", icon: CheckSquare, group: "ops", roles: ["super_admin", "hr", "hr_admin", "hr_maker"], badgeKey: "approvals_pending" },
  { to: "/exits", label: "Employee Exits", icon: UserMinus, group: "ops", roles: ["super_admin", "hr", "hr_admin", "hr_maker"] },
  { to: "/admin/requests", label: "Service Requests", icon: Ticket, group: "ops", roles: ["super_admin", "hr"] },
  { to: "/admin/approvals/requests", label: "Approval Queue", icon: CheckSquare, group: "ops", roles: ["super_admin", "hr"] },
  { to: "/admin/requests/analytics", label: "Request Analytics", icon: BarChart3, group: "ops", roles: ["super_admin", "hr"] },
  { to: "/delegation/tasks", label: "Delegate Tasks", icon: ClipboardList, group: "ops", roles: ["super_admin", "hr"] },
  { to: "/admin/checklist", label: "Compliance Checklist", icon: ListChecks, group: "ops", roles: ["super_admin", "mis", "hr", "ea", "senior_management"] },
  { to: "/checklist", label: "My Checklist", icon: ListChecks, group: "ops", roles: ["super_admin", "mis", "hr", "ea", "unit_manager", "accounts"] },
  { to: "/admin/sm-checklist", label: "SM Weekly Checklist", icon: ClipboardCheck, group: "ops", roles: ["super_admin", "mis", "ea"] },
  { to: "/admin/sm-checklist/monthly", label: "SM Monthly Summary", icon: BarChart3, group: "ops", roles: ["super_admin", "mis", "ea", "senior_management"] },
  { to: "/admin/compliance", label: "Statutory Compliance", icon: ShieldCheck, group: "ops", roles: ["super_admin", "senior_management", "hr", "accounts", "ea"] },
  { to: "/admin/broadcasts", label: "Broadcasts", icon: Megaphone, group: "ops", roles: ["super_admin", "hr", "senior_management", "hod"] },

  // ─── Consoles (role-specific) ────────────────────────────────────────
  { to: "/accounts/dashboard", label: "Accounts Console", icon: Calculator, group: "consoles", roles: ["super_admin", "hr", "accounts"] },
  { to: "/ea/dashboard", label: "EA Console", icon: ClipboardList, group: "consoles", roles: ["super_admin", "hr", "ea"] },
  { to: "/admin/ea-mappings", label: "EA Principal Mapping", icon: UserCog, group: "consoles", roles: ["super_admin", "mis"] },

  // ─── Reports ─────────────────────────────────────────────────────────
  { to: "/reports", label: "Reports", icon: FileBarChart2, group: "reports", roles: ["super_admin", "hr"] },

  // ─── Admin / Settings ────────────────────────────────────────────────
  { to: "/admin/company-settings", label: "Company Settings", icon: Building2, group: "settings", roles: ["super_admin"] },
  { to: "/settings/users", label: "Users", icon: ShieldCheck, group: "settings", roles: ["super_admin"] },
  { to: "/settings/audit", label: "Audit Log", icon: ScrollText, group: "settings", roles: ["super_admin"] },
  { to: "/settings/disaster-recovery", label: "Disaster Recovery", icon: LifeBuoy, group: "settings", roles: ["super_admin"] },
  { to: "/settings", label: "Settings", icon: Settings, group: "settings", roles: ["super_admin"] },
];

// Group ordering + metadata. `_top` items render before any group, ungrouped.
const NAV_GROUPS = [
  { key: "people", label: "People", icon: UsersGroupIcon },
  { key: "attendance", label: "Attendance", icon: Clock },
  { key: "leave", label: "Leave", icon: CalendarDays },
  { key: "payroll", label: "Payroll & Finance", icon: PiggyBank },
  { key: "performance", label: "Performance", icon: Star },
  { key: "logistics", label: "Logistics", icon: Truck },
  { key: "ops", label: "Operations", icon: Activity },
  { key: "consoles", label: "Consoles", icon: ClipboardList },
  { key: "reports", label: "Reports", icon: FileBarChart2 },
  { key: "settings", label: "Admin & Settings", icon: Wrench },
];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  hr: "HR",
  unit_manager: "Unit Manager",
  senior_management: "Senior Management",
  accounts: "Accounts",
  mis: "MIS",
  logistics_manager: "Logistics Manager",
  logistics_executive: "Logistics Executive",
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const role = user?.role;
  // Phase 15.9 — Fleet compliance alert count for sidebar badge + login toast.
  // Only fetched for roles allowed to see compliance dashboard.
  const [fleetAlerts, setFleetAlerts] = useState(null);
  // Phase B Session 3 — Approvals pending count for sidebar badge.
  // Polls every 60s so newly-submitted approvals surface promptly.
  const [approvalsPending, setApprovalsPending] = useState(0);
  useEffect(() => {
    const allowed = ["super_admin", "hr", "hr_admin", "hr_maker"];
    if (!user || !allowed.includes(role)) return;
    let cancelled = false;
    let interval;
    const load = async () => {
      try {
        const { data } = await api.get("/approvals/pending-count");
        if (!cancelled) setApprovalsPending(Number(data?.pending || 0));
      } catch { /* silent */ }
    };
    load();
    interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, role]);
  useEffect(() => {
    const allowed = ["super_admin", "senior_management", "hr",
                      "logistics_manager", "accounts", "ea"];
    if (!user || !allowed.includes(role)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/logistics/trucks/expiry-alerts?days=15");
        if (cancelled) return;
        setFleetAlerts(data);
        const total = (data?.expired || 0) + (data?.red || 0);
        // Show login toast once per session (per refresh).
        const seenKey = `fleet_alerts_toast_seen_${user.id}`;
        if (total > 0 && !sessionStorage.getItem(seenKey)) {
          sessionStorage.setItem(seenKey, "1");
          toast(
            (t) => (
              <div
                className="cursor-pointer"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate("/logistics/fleet-compliance");
                }}
              >
                <div className="font-semibold text-red-900 flex items-center gap-1">
                  <span>⚠️</span>
                  <span>{total} fleet document{total > 1 ? "s" : ""} need attention</span>
                </div>
                <div className="text-xs text-red-700 mt-0.5">
                  {data.expired > 0 && `${data.expired} expired`}
                  {data.expired > 0 && data.red > 0 && " · "}
                  {data.red > 0 && `${data.red} expiring in 15 days`}
                  <span className="block text-blue-700 mt-1 underline">View Compliance →</span>
                </div>
              </div>
            ),
            { duration: 10000, style: { background: "#fef2f2", border: "1px solid #fecaca" } }
          );
        }
      } catch {
        /* silently fail — compliance is non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [user, role, navigate]);

  // Items visible to the current user.
  // `hr_admin` and `hr_maker` are aliases of the classic `hr` role for
  // menu-visibility purposes — they were introduced later for the
  // maker-checker approval engine. Backend still enforces the finer
  // gate (approval-vs-submission) at the API layer.
  // Rev. 2026-08-05 — Defensive log if role is missing (rare corruption
  // path — DB `role` field wiped or JWT missing role claim). Helps HR
  // triage instead of showing a silent blank sidebar.
  useEffect(() => {
    if (user && (!role || role === "")) {
      console.error("AppLayout: user has NO role — sidebar will be empty. Contact Super Admin.", user);
    }
  }, [user, role]);

  const effectiveRoles = useMemo(() => {
    const set = new Set([role]);
    if (role === "hr_admin" || role === "hr_maker") set.add("hr");
    return set;
  }, [role]);

  const visibleItems = useMemo(() => NAV_ITEMS.filter(
    (i) => i.roles.some((r) => effectiveRoles.has(r)),
  ), [effectiveRoles]);

  // Search filter (flat list when searching, grouped otherwise)
  const searchLower = search.trim().toLowerCase();
  const filtered = searchLower
    ? visibleItems.filter((i) => i.label.toLowerCase().includes(searchLower))
    : visibleItems;

  // Compute active group based on the current path so it auto-opens
  const activeGroup = useMemo(() => {
    const match = visibleItems.find((i) =>
      i.to === "/" ? location.pathname === "/" : location.pathname.startsWith(i.to),
    );
    return match?.group;
  }, [location.pathname, visibleItems]);

  // Persisted collapsed-state per group (localStorage)
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nav_open_groups") || "{}");
      return saved;
    } catch {
      return {};
    }
  });
  const toggleGroup = (key) => {
    const next = { ...openGroups, [key]: !openGroups[key] };
    setOpenGroups(next);
    try {
      localStorage.setItem("nav_open_groups", JSON.stringify(next));
    } catch {
      /* no-op */
    }
  };
  const isGroupOpen = (key) =>
    openGroups[key] !== undefined ? !!openGroups[key] : key === activeGroup;

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const renderLink = (item) => {
    const Icon = item.icon;
    // Phase 15.9 — red badge for fleet compliance alerts.
    let badgeCount = 0;
    if (item.badgeKey === "fleet_alerts" && fleetAlerts) {
      badgeCount = (fleetAlerts.expired || 0) + (fleetAlerts.red || 0);
    }
    if (item.badgeKey === "approvals_pending") {
      badgeCount = approvalsPending;
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        onClick={() => setOpen(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `mx-2 my-0.5 flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
            isActive
              ? "nav-item-active"
              : "font-medium text-[#475569] hover:bg-slate-100 hover:text-slate-900"
          }`
        }
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="truncate flex-1">{item.label}</span>
        {badgeCount > 0 && (
          <span
            data-testid={`badge-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="bg-danger text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1"
          >
            {badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  const renderGroup = (group) => {
    const items = filtered.filter((i) => i.group === group.key);
    if (items.length === 0) return null;
    const Icon = group.icon;
    const opened = isGroupOpen(group.key);
    return (
      <div key={group.key} className="mb-1">
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          data-testid={`nav-group-${group.key}`}
          className="w-full mx-2 px-3 py-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
          style={{ width: "calc(100% - 1rem)" }}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">{group.label}</span>
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${
              opened ? "rotate-90" : ""
            }`}
          />
          <span className="text-slate-600 text-[10px] font-mono">{items.length}</span>
        </button>
        {opened && <div className="mt-0.5">{items.map(renderLink)}</div>}
      </div>
    );
  };

  const SidebarBody = () => (
    <>
      <div className="px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
        <div className="brand-accent-bar rounded-full mb-3 -mt-1" data-testid="brand-accent-bar-sidebar" />
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-md bg-white border border-[var(--border)] flex items-center justify-center p-1 flex-shrink-0">
            <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="brand-text font-bold text-sm leading-tight" style={{ fontFamily: "Chivo" }}>
              Bravura Packers
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HR Console</div>
          </div>
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search menu…"
            data-testid="nav-search"
            className="w-full bg-slate-100 border border-slate-200 rounded-md pl-7 pr-7 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[var(--primary)]"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="nav-search-clear"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        {/* Top items (Overview etc.) — always visible, ungrouped */}
        {filtered.filter((i) => i.group === "_top").map(renderLink)}

        {searchLower ? (
          // While searching: show all matches flat, group headers hidden
          filtered.filter((i) => i.group !== "_top").length > 0 ? (
            <div className="mt-2">
              {filtered.filter((i) => i.group !== "_top").map(renderLink)}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-xs text-slate-500">
              No matches for "{search}"
            </div>
          )
        ) : (
          <div className="mt-2">
            {NAV_GROUPS.map(renderGroup)}
          </div>
        )}
      </nav>
      <div className="border-t border-[var(--border)] p-4 space-y-2 flex-shrink-0">
        <div>
          <div className="text-xs text-slate-400 mb-1">Signed in as</div>
          <div className="text-sm text-slate-900 font-medium" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role]}</div>
        </div>
        <PrincipalSwitcher tone="light" />
        <button
          data-testid="sidebar-logout-btn"
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-100 hover:text-slate-900 rounded-md transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-64 bg-white text-[#475569] border-r border-[var(--border)] flex-col flex-shrink-0"
      >
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        data-testid="sidebar-mobile"
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white text-[#475569] border-r border-[var(--border)] flex flex-col transform transition-transform duration-200 ease-out lg:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarBody />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 lg:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-700 hover:bg-slate-100 rounded"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img src="/bravura_logo.png" alt="Bravura" className="h-9 w-9 object-contain hidden sm:block" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">
                Bravura Packers Pvt Ltd
              </div>
              <div className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: "Chivo" }}>
                HR &amp; Payroll
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Phase 15.15 — Switch to My Portal: shown for everyone except
               drivers. Drivers have their own /driver mobile shell. Other
               users (admin/HR/SM/HOD) may have a linked employee record
               for self-service; /my-portal will surface it. */}
            {user?.category !== "driver" && (
              <button
                onClick={() => navigate("/my-portal/dashboard")}
                data-testid="header-switch-to-portal"
                className="hidden sm:flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-700 ring-1 ring-red-200 hover:bg-red-100 transition"
                title="Switch to my employee portal"
              >
                <User className="h-3.5 w-3.5" /> My Portal
              </button>
            )}
            <BirthdayBell />
            <AnniversaryBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <div
              className="h-9 w-9 rounded-full brand-bg text-white flex items-center justify-center text-sm font-bold"
              data-testid="header-user-avatar"
            >
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
